import React, { useContext, useEffect, useState, useMemo } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UserContext } from '../context/UserContext';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import { getEnvironmentAsEnum } from '../utils/environment';
import { PageWithAnalytics } from '../components/common/analytics-wrapped/PageWithAnalytics';
import DocumentHead from '../components/common/DocumentHead';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../utils/constants';
import { championsAuthSignupIsActive } from '../services/statsig/featureGates';
import ChampionApplicationForm from '../components/champions/ChampionApplicationForm';
import { getEntryPointFromUrl } from '../analytics/champions-helpers';
import {
  getCombinedChampionInitData,
  type UnifiedAccount,
  type UnifiedProfile,
  type UnifiedChampionPicklistFields,
} from '../services/http/ChampionGraphQlOperations';
import type { ChampionPicklistField } from '../types/ChampionApplicationFormTypes';

const REQUIRED_PICKLIST_FIELD_NAMES = new Set<string>([
  'Contribution_Type__c',
  'Partner_Type__c',
  'Leverage_for_Atlassian_training__c',
  'Products_used__c',
  'Training_on_Atlassian_productAndSolution__c',
  'Focus_Of_Excellence__c',
  'Events_per_year__c',
  'Discuss_Atlassian_Excellence__c',
  'Active_Atlassian_User_Group__c',
  'Content_Sharing_Frequency__c',
  'Can_you_commit_to_4_events_per_year__c',
  'How_did_you_find_out_about_program__c',
  'Country__c',
]);

const validateRequiredPicklists = (allFields: ChampionPicklistField[]): boolean => {
  const fieldsByName = new Map(allFields.map((f) => [f.name, f]));

  for (const requiredName of REQUIRED_PICKLIST_FIELD_NAMES) {
    const field = fieldsByName.get(requiredName);

    if (!field || !field.picklistValues || field.picklistValues.length === 0) {
      return false;
    }
  }

  return true;
};

const isUnifiedChampionPicklistFieldsSuccess = (
  fields: UnifiedChampionPicklistFields | undefined,
): fields is Exclude<UnifiedChampionPicklistFields, { code?: string }> => {
  return !!fields && !('code' in fields);
};

const isUnifiedAccountSuccess = (
  account: UnifiedAccount | undefined,
): account is Exclude<UnifiedAccount, { code?: string }> => {
  return !!account && !('code' in account);
};

const isUnifiedProfileSuccess = (
  profile: UnifiedProfile | undefined,
): profile is Exclude<UnifiedProfile, { code?: string }> => {
  return !!profile && !('code' in profile);
};

const TITLE = 'Champions Apply | Atlassian Community';
const DESCRIPTION = 'Apply to become an Atlassian Champion.';
const CHAMPIONS_APPLY_PATH = '/champions/apply';

const getApplyRedirectUrl = () => `${process.env.HOME_BASE_URL}${CHAMPIONS_APPLY_PATH}`;

type AccountInfo = {
  emailId: string;
  profileId: string;
  isManaged: boolean;
};

export const ChampionsApplyPage: React.FC = () => {
  const { user, userIsInitialized } = useContext(UserContext) ?? {};
  const isLoggedIn = !!user;
  const isAuthSignupEnabled = championsAuthSignupIsActive();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [picklistFields, setPicklistFields] = useState<ChampionPicklistField[]>([]);
  const [accountError, setAccountError] = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const homepage =
    process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  const entryPoint = useMemo(() => getEntryPointFromUrl(), []);
  const userId = user?.account_id;

  const screenAttributes = useMemo(() => ({
    entryPoint,
    referrer: document.referrer || 'none',
    featureGateEnabled: isAuthSignupEnabled,
    pagePath: CHAMPIONS_APPLY_PATH,
    isAuthenticated: !!userId,
    userId: userId || 'anonymous',
  }), [entryPoint, isAuthSignupEnabled, userId]);

  useEffect(() => {
    if (!isAuthSignupEnabled) {
      window.location.href = DEFAULT_CHAMPIONS_SIGNUP_URL;
      return;
    }

    if (!userIsInitialized || !isLoggedIn) {
      window.location.href = getCommunityLoginUrl(
        { redirectUrl: getApplyRedirectUrl(), experience: 'champion-apply' },
        getEnvironmentAsEnum(),
      );
      return;
    }

    if (!user?.account_id) {
      setAccountError(true);
      setAccountLoading(false);
      return;
    }

    let cancelled = false;

    const fetchChampionInitData = async () => {
      try {
        const response = await getCombinedChampionInitData(user.account_id, homepage, homepage);

        if (cancelled || !response) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        const account = response.data?.userAccount?.account;
        const profile = response.data?.userProfile?.unifiedProfile;
        const picklist = response.data?.championSignupPicklist?.championPicklist?.getChampionSignupPicklistFields;

        // Validate account
        if (!isUnifiedAccountSuccess(account) || !account.emailId) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        // Validate profile
        if (!isUnifiedProfileSuccess(profile) || !profile.internalId) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        // Validate picklists
        if (!isUnifiedChampionPicklistFieldsSuccess(picklist) || !picklist.fields) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        if (!validateRequiredPicklists(picklist.fields)) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        if (cancelled) return;

        setAccountInfo({
          emailId: account.emailId,
          profileId: profile.internalId,
          isManaged: account.isManaged === true,
        });
        setPicklistFields(picklist.fields);
        setAccountLoading(false);
      } catch {
        if (!cancelled) {
          setAccountError(true);
          setAccountLoading(false);
        }
      }
    };

    fetchChampionInitData();

    return () => {
      cancelled = true;
    };
  }, [isAuthSignupEnabled, userIsInitialized, isLoggedIn, user?.account_id, homepage]);

  const canShowContent = isAuthSignupEnabled && userIsInitialized && isLoggedIn;

  if (!canShowContent || accountLoading) {
    return (
      <div css={centeredPageStyles}>
        <div css={containerStyles}>
          <p css={descriptionStyles}>Loading...</p>
        </div>
      </div>
    );
  }

  if (accountError || !accountInfo) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={errorBannerStyles}>Oops! Something went wrong.</p>
        </div>
      </div>
    );
  }

  return (
    <PageWithAnalytics eventScreenName="championsApplyScreen" attributes={screenAttributes}>
      <DocumentHead title={TITLE} description={DESCRIPTION} />
      <div css={pageStyles}>
        <div style={{ width: '100%' }}>
          <ChampionApplicationForm
            aaid={user?.account_id ?? ''}
            accountIsManaged={accountInfo.isManaged}
            emailId={accountInfo.emailId}
            profileId={accountInfo.profileId}
            userName={user?.name ?? ''}
            homeBaseUrl={homepage ?? ''}
            userId={user?.account_id}
            entryPoint={entryPoint}
            picklistFields={picklistFields}
          />
        </div>
      </div>
    </PageWithAnalytics>
  );
};

export default ChampionsApplyPage;

const containerStyles = css({
  maxWidth: '800px',
  minWidth: '375px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: token('space.600'),
});

const descriptionStyles = css({
  marginBottom: token('space.200'),
  color: token('color.text'),
  fontFamily: 'Inter',
  fontWeight: 400,
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '18px',
});

const pageStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.400'), /* half distance from navbar */
  paddingBottom: token('space.800'),
  minHeight: '400px',
});

const centeredPageStyles = css({
  display: 'flex',
  width: '100%',
  minHeight: '400px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: token('space.400'),
  paddingBottom: token('space.800'),
});

const errorBannerStyles = css({
  color: '#3e3e3c',
  fontSize: '18px',
  fontWeight: 400,
  textAlign: 'center',
  lineHeight: '24px',
});

